import { discoveryDocumentUrl } from './authority';

/**
 * Ending a Crossmarc session properly, in one place (docs/29 F15).
 *
 * THE BUG THIS EXISTS TO FIX. Sign-out used to build Cognito's `/logout` URL from
 * `VITE_COGNITO_DOMAIN`, a value published by appcore CDK (`deploy/instances/<env>.json`
 * -> `/appcore/<env>/cognito/hosted-ui-domain`) and baked into the bundle at build time.
 * Sign-IN never used that value: `oidcConfig.authority` points at the pool's OIDC
 * discovery document, and oidc-client-ts redirects to whatever `authorization_endpoint`
 * that document reports.
 *
 * On 2026-07-24 crossmarc-auth added a branded custom hosted-UI domain to all three
 * pools (`auth[-<env>].crossmarc.com`) ALONGSIDE the AWS-assigned prefix domain. That is
 * additive for the pool, but it is NOT additive for the discovery document: Cognito
 * immediately began reporting the CUSTOM domain as `authorization_endpoint` /
 * `end_session_endpoint`. So sign-in moved to the custom host and set its SSO cookie
 * there, while sign-out kept calling `/logout` on the old prefix host. Cognito's session
 * cookies are host-only (no `Domain=` attribute), and the two hosts are not even the same
 * registrable domain, so `/logout` cheerfully returned its 302 to `logout_uri` while
 * clearing nothing that mattered. The user landed on `/login` looking signed out, and the
 * next "Sign in" was silently SSO'd straight back through `/callback`.
 *
 * THE FIX, AND WHY IT IS SHAPED THIS WAY. Both endpoints are read from the same discovery
 * document URL sign-in uses (a separate fetch, see the origin check in
 * {@link resolveEndSessionEndpoints}), so the two halves normally agree about which host
 * holds the cookie. `VITE_COGNITO_DOMAIN` survives only as a fallback for when discovery
 * fails or omits a field, which is no worse than the old behaviour. When discovery names a
 * `/logout` on a different origin from `authorization_endpoint`, the sign-in origin's
 * `/logout` is used instead (#713).
 *
 * A dated reading, not an invariant: on 2026-10-06 the coordinating session read the public
 * discovery documents for the pool ids in the served dev, stage and prod SPA bundles; in each,
 * the authorization, end_session and revocation endpoints were on one https host and
 * `revocation_endpoint` was present.
 *
 * Do NOT "simplify" this back to a hardcoded host, and do not switch to
 * `oidc-client-ts`'s spec-compliant `signoutRedirect()`: Cognito's `/logout` is not
 * RP-initiated-logout compliant. It wants `client_id` + `logout_uri` (verified by probe:
 * both hosts 302 to `logout_uri`), and `logout_uri` must exactly match a registered
 * LogoutURL on the app client.
 */

/** The two endpoints ending a session needs. Either may be absent if discovery failed
 *  and no `VITE_COGNITO_DOMAIN` fallback is configured. */
export interface EndSessionEndpoints {
  /** Cognito's `/logout`. The ONLY thing that clears the Hosted-UI SSO cookie. */
  endSession?: string;
  /** Cognito's `/oauth2/revoke`. Kills the refresh token and every access token
   *  minted from it. */
  revocation?: string;
}

/** How long to wait for the discovery document before falling back. Sign-out must never
 *  hang behind a slow network: a user who clicked "Sign out" and sees nothing happen
 *  assumes the app is broken, and worse, may walk away from a live session. */
const DISCOVERY_TIMEOUT_MS = 3_000;

let cached: EndSessionEndpoints | null = null;
/** The lookup currently in flight, so a sign-out that lands while
 *  {@link prewarmEndSessionEndpoints} is still running JOINS it instead of starting a
 *  second request and waiting on that. Without this the pre-warm buys nothing in exactly
 *  the case it exists for: a click during app start. */
let inFlight: Promise<EndSessionEndpoints> | null = null;

/** Test seam. Never call from app code. */
export function resetEndSessionEndpointsCache(): void {
  cached = null;
  inFlight = null;
}

/** Last-resort endpoints derived from the build-time hosted-UI domain. Correct only while
 *  that value tracks the pool's real OAuth host, which is exactly the assumption F15
 *  proved fragile, so this is a fallback and never the primary source. */
function fallbackEndpoints(): EndSessionEndpoints {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN;
  if (!domain) return {};
  return {
    endSession: `https://${domain}/logout`,
    revocation: `https://${domain}/oauth2/revoke`,
  };
}

/**
 * Announce that we are about to sign out against a build-time host instead of the one the
 * IdP currently advertises.
 *
 * This warning is not cosmetic. Falling back re-creates the exact F15 failure whenever the
 * build-time value has gone stale, and that failure is INDISTINGUISHABLE FROM SUCCESS from
 * the outside: the wrong host still answers `/logout` with a clean 302, the user still
 * lands on `/login`, and only the next sign-in reveals the session never ended. Silent
 * degradation into the bug we just fixed is the one outcome this module must not have, so
 * the degraded path leaves a trace naming the host it used.
 */
function warnFallback(reason: string, endpoints: EndSessionEndpoints): void {
  const host = endpoints.endSession ?? '(none configured)';
  console.warn(
    `[auth] OIDC discovery unavailable (${reason}); signing out against the build-time ` +
      `hosted-UI host instead: ${host}. If that host is stale, sign-out will NOT end the ` +
      `Cognito session (docs/29 F15).`,
  );
}

/**
 * The revocation half of {@link warnFallback}. Revocation is pool-level rather than
 * cookie-host-level, so a stale build-time host that still fronts the same pool revokes
 * correctly; the failure this guards is a stale host that no longer resolves to the pool, in
 * which case the refresh token survives sign-out for its TTL (docs/29 F15b). Names the
 * endpoint, why discovery did not supply it, and the ORIGIN used instead (never a full URL).
 */
function warnRevocationFallback(reason: string, endpoints: EndSessionEndpoints): void {
  const host = originOf(endpoints.revocation) ?? '(none configured, so no revocation will be sent)';
  console.warn(
    `[auth] OIDC discovery gave no usable revocation_endpoint (${reason}); revoking against ` +
      `the build-time hosted-UI host instead: ${host}. If that host no longer fronts this ` +
      `pool, the refresh token will NOT be revoked at sign-out (docs/29 F15b).`,
  );
}

/** The origin of an absolute URL, or undefined if it does not parse. Used so a warning can
 *  name a host without echoing a path or query string. */
function originOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

/** Why a discovery field was not usable: the document did not say it, or said something
 *  {@link httpsOnly} rejects. */
function whyUnusable(raw: unknown): string {
  return raw === undefined || raw === null || raw === '' ? 'absent' : 'not an https URL';
}

/** Only ever navigate to, or POST a refresh token at, an `https:` URL. The discovery
 *  document is fetched over TLS from the IdP, so this should be unreachable, but the two
 *  values below are used as a top-level navigation target and as the destination for a
 *  live credential: a `javascript:` endpoint would be script execution and an `http:` one
 *  would put the refresh token on the wire in clear. Cheap, absolute, worth it. */
function httpsOnly(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolve `/logout` and `/oauth2/revoke` from the pool's OIDC discovery document.
 *
 * Never throws and never rejects: every failure path degrades to {@link fallbackEndpoints}
 * (loudly, see {@link warnFallback}) so the caller can always still navigate somewhere. The
 * one result that is neither discovered nor a build-time fallback is a `/logout` synthesised
 * on the sign-in origin when discovery's `/logout` is on a different origin (see the check
 * below).
 *
 * Memoisation is on a successful FETCH, which includes a response that simply omits a field
 * (a document that does not advertise `revocation_endpoint` will not start advertising it on
 * a retry, so re-fetching buys nothing). A FAILED fetch is never memoised, so a sign-out
 * attempted while briefly offline cannot poison later attempts.
 */
export async function loadEndSessionEndpoints(
  timeoutMs: number = DISCOVERY_TIMEOUT_MS,
): Promise<EndSessionEndpoints> {
  if (cached) return cached;
  // Join an in-flight lookup rather than racing it. A joiner inherits the original
  // timeout budget, which is what we want: it is already partway through.
  if (inFlight) return inFlight;

  inFlight = resolveEndSessionEndpoints(timeoutMs);
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

async function resolveEndSessionEndpoints(
  timeoutMs: number,
): Promise<EndSessionEndpoints> {
  const fallback = fallbackEndpoints();
  const url = discoveryDocumentUrl();
  if (!url) {
    warnFallback('no user pool configured', fallback);
    return fallback;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) {
      warnFallback(`discovery responded ${res.status}`, fallback);
      return fallback;
    }
    const doc = (await res.json()) as Record<string, unknown>;
    // Keep what discovery actually SAID separate from what we ended up USING. Provenance,
    // not string equality, is what decides whether we fell back: the healthy steady state
    // is that discovery and `VITE_COGNITO_DOMAIN` name the SAME host, so comparing the two
    // values declared a fallback on every load of a correctly configured build, and the
    // warning that exists to make a real fallback impossible to miss became background
    // noise. (The suite below missed it because every case there uses a domain that is
    // deliberately drifted from the discovery host.)
    const discovered = {
      endSession: httpsOnly(doc['end_session_endpoint'] as string | undefined),
      revocation: httpsOnly(doc['revocation_endpoint'] as string | undefined),
    };
    const resolved: EndSessionEndpoints = {
      endSession: discovered.endSession ?? fallback.endSession,
      revocation: discovered.revocation ?? fallback.revocation,
    };
    if (!discovered.revocation) {
      warnRevocationFallback(whyUnusable(doc['revocation_endpoint']), fallback);
    }
    // The F15 invariant, stated as a check on ORIGINS: the origin that ends the session
    // should be the origin sign-in sends the browser to, because the hosted-UI session
    // cookie is host-scoped and is normally set by the host that served
    // `authorization_endpoint`. Sign-in reads that value with oidc-client-ts from the SAME
    // URL this module fetches, but as a SEPARATE fetch with a separate in-memory cache,
    // possibly on an earlier page load (the user store is localStorage). So the check holds
    // against the document as of THIS load: if the pool's domain changed between the
    // sign-in page load and this one, it passes while the cookie sits on the old host.
    // "No override" on the sign-in side is pinned by a test on `oidcConfig` and
    // `buildLinkPopupConfig()` (no `metadata`, `metadataSeed`, `metadataUrl`). A port
    // difference counts as a mismatch although cookies are not port-scoped; that is
    // deliberately conservative and unchanged. This also covers a build-time FALLBACK
    // `/logout` that differs from the sign-in origin.
    const signInOrigin = originOf(httpsOnly(doc['authorization_endpoint'] as string | undefined));
    const logoutOrigin = originOf(resolved.endSession);
    const replaceLogout = !!signInOrigin && !!logoutOrigin && logoutOrigin !== signInOrigin;
    // When the build-time host is about to be replaced, its own fallback warning ("signing
    // out against the build-time host") would be false, so the replacement warning below
    // stands in for it and gives the same reason.
    if (!discovered.endSession && !replaceLogout) {
      warnFallback('discovery advertised no usable end_session_endpoint', fallback);
    }
    if (replaceLogout) {
      console.warn(
        `[auth] ${
          discovered.endSession
            ? "Discovery's end_session_endpoint"
            : 'Discovery advertised no usable end_session_endpoint, and the build-time hosted-UI host'
        } (${logoutOrigin}) is not the origin sign-in uses (${signInOrigin}, from ` +
          `authorization_endpoint). Ending the session on the sign-in origin instead, because ` +
          `the hosted-UI session cookie is host-scoped and sign-in sends the browser there ` +
          `(docs/29 F15).`,
      );
      // Hardcoded `/logout`: right for Cognito, which this module is built around. For an
      // IdP that legitimately puts end-session on another host or path (config.ts invites
      // "any OIDC-compliant IdP"), this sends the user to a path that may not exist there,
      // where the advertised endpoint would have been followed before this check existed.
      resolved.endSession = `${signInOrigin}/logout`;
    } else if (!signInOrigin && logoutOrigin) {
      // No usable authorization_endpoint to compare against: say so rather than imply the
      // check passed. The resolution is left as it was.
      console.warn(
        `[auth] Cannot cross-check the /logout origin (${logoutOrigin}) against the sign-in ` +
          `origin: discovery's authorization_endpoint is ${whyUnusable(doc['authorization_endpoint'])} ` +
          `(docs/29 F15).`,
      );
    }
    cached = resolved;
    return resolved;
  } catch (err) {
    // Includes the abort: a discovery document that has not answered within the budget is
    // treated the same as one that is not there.
    warnFallback(err instanceof Error ? err.name : 'discovery request failed', fallback);
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch and memoise the discovery document at app start, so sign-out never has to wait on
 * a live network call at the moment it is clicked.
 *
 * Two things depend on this, not one. The obvious one is latency: a cold lookup adds up to
 * {@link DISCOVERY_TIMEOUT_MS} of dead air to a click. The subtler one is that
 * {@link endCognitoSession} clears the local session BEFORE it resolves the endpoints (so
 * the click has immediate visible effect), which opens a window in which the app looks
 * signed out while the Cognito session is still live. Pre-warming keeps that window at
 * effectively zero. Fire-and-forget by design: a failure here just means the sign-out path
 * retries the lookup itself.
 */
export function prewarmEndSessionEndpoints(): void {
  void loadEndSessionEndpoints().catch(() => {
    // loadEndSessionEndpoints never rejects; this is belt-and-braces so a future change
    // cannot turn app start into an unhandled rejection.
  });
}

/**
 * Revoke the refresh token, best-effort and deliberately un-awaited.
 *
 * Cognito revokes the refresh token AND every access token minted from it, which is the
 * only reason this matters: prod refresh tokens live 24 hours, so without this a token
 * captured before sign-out outlives the sign-out by a day (docs/29 F15b).
 *
 * `keepalive` is load-bearing, not decoration: the caller navigates to `/logout`
 * immediately after this returns, and a normal fetch would be cancelled by that
 * navigation. `keepalive` lets the POST outlive the document, which is what makes
 * "fire and forget" honest rather than "fire and hope". It also means a hung or failing
 * revoke can never delay or block the redirect, so a revoke outage cannot leave a user
 * stuck on a page that will not sign out.
 *
 * The client is public (no secret), so `client_id` in the form body is the whole of the
 * client authentication Cognito expects here.
 */
export function revokeRefreshToken(args: {
  revocationEndpoint?: string;
  clientId?: string;
  refreshToken?: string | null;
}): void {
  const { clientId, refreshToken } = args;
  // https only: this POSTs a live credential, so an `http:` endpoint would put the refresh
  // token on the wire in clear.
  const revocationEndpoint = httpsOnly(args.revocationEndpoint);
  if (!revocationEndpoint || !clientId || !refreshToken) return;
  try {
    void fetch(revocationEndpoint, {
      method: 'POST',
      // A CORS-safelisted content type, so this needs no preflight.
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: refreshToken, client_id: clientId }).toString(),
      keepalive: true,
    }).catch(() => {
      // Best-effort by contract. The redirect below is the control that matters.
    });
  } catch {
    // fetch() itself can throw synchronously on a malformed URL. Same contract.
  }
}

export interface EndCognitoSessionArgs {
  /** Read from the live user BEFORE the local store is cleared, or null if absent. */
  refreshToken: string | null;
  clientId?: string;
  /** Where Cognito should bounce back to. Must be a registered LogoutURL on the client. */
  logoutUri: string;
  /** Clears the local OIDC token store (`auth.removeUser()`). */
  clearLocalSession: () => Promise<unknown>;
  /** Performs the top-level navigation (`window.location.assign`). */
  navigate: (url: string) => void;
}

/**
 * The whole sign-out sequence, in order, with every step failure-tolerant.
 *
 * ORDER IS DELIBERATE, and the first step is the one to think twice about:
 *
 * 1. **Clear the local token store first.** The caller has already captured the refresh
 *    token, so this costs nothing in security, and it is the only step with an immediate
 *    visible effect: `ProtectedRoute` reacts and the user sees the app drop to `/login`.
 *    Resolving discovery first instead put up to {@link DISCOVERY_TIMEOUT_MS} of dead air
 *    between the click and any feedback, which is exactly the "user assumes the app is
 *    broken" failure this module warns about elsewhere.
 *
 *    The trade-off, stated plainly: between this step and the redirect there is a window
 *    where the app looks signed out while the Cognito session is still live, and a page
 *    reload inside that window would abandon the redirect and leave the SSO cookie, i.e.
 *    the F15 symptom. {@link prewarmEndSessionEndpoints} is what makes that window
 *    effectively zero in practice, so the two changes belong together: do not remove the
 *    pre-warm without reverting this ordering.
 * 2. **Revoke**, un-awaited and `keepalive`, so it neither delays step 3 nor needs the page
 *    to survive.
 * 3. **Redirect to Cognito's `/logout`**, the only thing that clears the SSO cookie.
 *
 * The navigation is unconditional. Nothing above it may prevent it, because a user who
 * cannot sign out is a worse outcome than a missed revoke or a stale local token.
 */
export async function endCognitoSession(args: EndCognitoSessionArgs): Promise<void> {
  const { refreshToken, clientId, logoutUri, clearLocalSession, navigate } = args;

  try {
    await clearLocalSession();
  } catch {
    // Clearing the local store must not block ending the Cognito session. Without the
    // redirect below the user is not signed out at all; without this they merely keep a
    // dead token the next request would reject.
  }

  const endpoints = await loadEndSessionEndpoints();

  revokeRefreshToken({ revocationEndpoint: endpoints.revocation, clientId, refreshToken });

  const endSession = httpsOnly(endpoints.endSession);
  if (endSession && clientId) {
    const params = new URLSearchParams({ client_id: clientId, logout_uri: logoutUri });
    navigate(`${endSession}?${params.toString()}`);
    return;
  }
  // No usable hosted-UI endpoint: local-only sign-out. The Cognito session survives, so
  // this is a degraded outcome, but it is the same one the pre-F15 fallback produced.
  navigate(logoutUri);
}
